import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { stockAlertsAPI, StockAlert, StockAlertStatus, StockAlertType } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  FunnelIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Badge, Button, Skeleton, Modal } from '../ui';

const getAlertTypeColor = (type: StockAlertType): 'error' | 'warning' | 'primary' | 'neutral' => {
  switch (type) {
    case 'OUT_OF_STOCK': return 'error';
    case 'LOW_STOCK': return 'warning';
    case 'BACKORDER_NEEDED': return 'error';
    case 'REORDER_POINT': return 'warning';
    case 'STOCK_RESERVED': return 'primary';
    default: return 'neutral';
  }
};

const getStatusColor = (status: StockAlertStatus): 'error' | 'warning' | 'success' | 'neutral' => {
  switch (status) {
    case 'ACTIVE': return 'error';
    case 'ACKNOWLEDGED': return 'warning';
    case 'RESOLVED': return 'success';
    case 'DISMISSED': return 'neutral';
    default: return 'neutral';
  }
};

const formatType = (type: string): string => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const StockAlertsView: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<StockAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StockAlertStatus | ''>('ACTIVE');
  const [typeFilter, setTypeFilter] = useState<StockAlertType | ''>('');
  const [selectedAlert, setSelectedAlert] = useState<StockAlert | null>(null);

  const isAdminContext = location.pathname.startsWith('/admin');
  const basePath = isAdminContext ? '/admin' : '/app';

  useEffect(() => {
    fetchAlerts();
  }, [statusFilter, typeFilter]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const result = await stockAlertsAPI.getAll({
        status: statusFilter || undefined,
        alertType: typeFilter || undefined,
        limit: 100,
      });
      setAlerts(result.alerts);
      setTotal(result.total);
    } catch (err) {
      setError('Failed to load stock alerts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (alertId: number, status: StockAlertStatus) => {
    try {
      await stockAlertsAPI.updateStatus(alertId, status, user?.id);
      fetchAlerts();
      setSelectedAlert(null);
    } catch (err) {
      console.error('Failed to update alert status:', err);
    }
  };

  const statusOptions: StockAlertStatus[] = ['ACTIVE', 'ACKNOWLEDGED', 'RESOLVED', 'DISMISSED'];
  const typeOptions: StockAlertType[] = ['LOW_STOCK', 'OUT_OF_STOCK', 'BACKORDER_NEEDED', 'REORDER_POINT'];

  if (loading && alerts.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-neutral-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StockAlertStatus | '')}
            className="rounded-md border-neutral-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>{formatType(status)}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as StockAlertType | '')}
            className="rounded-md border-neutral-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            {typeOptions.map((type) => (
              <option key={type} value={type}>{formatType(type)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={fetchAlerts}
          className="p-2 text-neutral-500 hover:text-neutral-700 rounded-md hover:bg-neutral-100"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
        <span className="text-sm text-neutral-500">{total} alerts</span>
      </div>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-lg">{error}</div>
      )}

      {alerts.length === 0 ? (
        <Card>
          <CardBody className="text-center py-12">
            <CheckCircleIcon className="h-12 w-12 text-success-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900">No Active Alerts</h3>
            <p className="text-neutral-500">All inventory levels are healthy</p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {alerts.map((alert) => (
            <Card key={alert.id} className="hover:shadow-md transition-shadow">
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className={`h-5 w-5 ${
                      alert.alertType === 'OUT_OF_STOCK' ? 'text-error-500' : 'text-warning-500'
                    }`} />
                    <Badge variant={getAlertTypeColor(alert.alertType)} size="sm">
                      {formatType(alert.alertType)}
                    </Badge>
                  </div>
                  <Badge variant={getStatusColor(alert.status)} size="sm">
                    {formatType(alert.status)}
                  </Badge>
                </div>
                <div className="mt-3">
                  <h4 className="font-medium text-neutral-900">
                    {alert.product?.name || `Product #${alert.productId}`}
                  </h4>
                  {alert.product?.sku && (
                    <p className="text-xs text-neutral-500">SKU: {alert.product.sku}</p>
                  )}
                </div>
                <p className="mt-2 text-sm text-neutral-600">{alert.message}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
                  <span>Current: {alert.currentQty} | Required: {alert.requiredQty || '-'}</span>
                  {alert.deal && (
                    <Link
                      to={`${basePath}/deals/${alert.dealId}`}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {alert.deal.orderNumber || `Order #${alert.dealId}`}
                    </Link>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between">
                  <span className="text-xs text-neutral-400">{formatDate(alert.createdAt)}</span>
                  {alert.status === 'ACTIVE' && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleUpdateStatus(alert.id, 'ACKNOWLEDGED')}
                        className="p-1 text-warning-600 hover:bg-warning-50 rounded"
                        title="Acknowledge"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(alert.id, 'RESOLVED')}
                        className="p-1 text-success-600 hover:bg-success-50 rounded"
                        title="Resolve"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(alert.id, 'DISMISSED')}
                        className="p-1 text-neutral-600 hover:bg-neutral-100 rounded"
                        title="Dismiss"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StockAlertsView;

